import React, { useState, useEffect } from 'react';
import { Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'antd';
import axios from 'axios';
import {
  FaTachometerAlt,
  FaBox,
  FaWarehouse,
  FaChartLine,
  FaClipboardList,
  FaReceipt,
} from "react-icons/fa";

// Pages
import DashboardPage from './DashboardPage';
import ProductsPage from './ProductsPage';
import ProductStocks from './ProductStocks';
import SalesManagement from './SalesManagement';
import ProfileEditPage from './ProfileEditPage';
import RequestsPage from './RequestPage';
import ViewReceiptPage from './ViewReceiptPage';
import SalesListAndReport from './SalesListAndReport';

function ProductionDashboardLayout() {
  const navigate = useNavigate();

  const [activeMenu, setActiveMenu] = useState(localStorage.getItem("activeMenu") || "dashboard-page");
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [notifications] = useState(3);

  useEffect(() => localStorage.setItem("activeMenu", activeMenu), [activeMenu]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const fetchAdminData = async () => {
    try {
      const res = await axios.get('https://agpipefitting.org.et/api/admin/profile');
      setAdminData(res.data);
      localStorage.setItem('admin', JSON.stringify(res.data));
    } catch {
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  const stored = localStorage.getItem('admin');

  if (stored) {
    setAdminData(JSON.parse(stored));
    setLoading(false);
  } else {
    fetchAdminData();
  }
}, []);


  const role = adminData?.role || "Admin";

  const SidebarItem = ({ icon, label, menuKey }) => (
    <div
      onClick={() => setActiveMenu(menuKey)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px",
        borderRadius: "8px",
        cursor: "pointer",
        marginBottom: "6px",
        background: activeMenu === menuKey ? "rgba(56,189,248,0.15)" : "transparent",
        color: activeMenu === menuKey ? "#38bdf8" : darkMode ? "#cbd5e1" : "#334155",
        justifyContent: collapsed ? "center" : "flex-start",
        transition: "0.2s"
      }}
    >
      <span style={{ fontSize: "18px" }}>{icon}</span>
      {!collapsed && <span>{label}</span>}
    </div>
  );

  const renderContent = () => {
    switch (activeMenu) {
      case "dashboard-page": return <DashboardPage />;
      case "products": return <ProductsPage />;
      case "stock": return <ProductStocks />;
      case "sales": return <SalesManagement />;
      case "sales-list-and-report": return <SalesListAndReport />;
      case "requests": return <RequestsPage />;
      case "view-receipt": return <ViewReceiptPage />;
      case "edit-profile": return <ProfileEditPage adminData={adminData} onProfileUpdate={setAdminData} />;
      default: return <DashboardPage />;
    }
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>Loading...</div>;

  return (
    <div className="d-flex">

      {/* Sidebar */}
      <div style={{
        width: collapsed ? "80px" : "260px",
        height: "100vh",
        position: "fixed",
        background: darkMode ? "linear-gradient(180deg,#0f172a,#1e293b)" : "#ffffff",
        color: "#fff",
        padding: "20px 10px",
        transition: "0.3s",
        boxShadow: "4px 0 15px rgba(0,0,0,0.15)"
      }}>
        <div className="text-end mb-3">
          <button onClick={() => setCollapsed(!collapsed)} style={{ background: "none", border: "none", color: "#38bdf8", fontSize: "20px" }}>☰</button>
        </div>

        {!collapsed && (
          <div className="text-center mb-4">
            <h5 style={{ color: "#38bdf8", fontWeight: 600 }}>AG PIPE Inventory SYSTEM</h5>
            <small style={{ color: "#94a3b8" }}>Stock Dashboard</small>
          </div>
        )}

        <Nav className="flex-column">
          <SidebarItem icon={<FaTachometerAlt />} label="Dashboard" menuKey="dashboard-page" />
          <SidebarItem icon={<FaBox />} label="Products Receiving" menuKey="products" />
          <SidebarItem icon={<FaWarehouse />} label="Products Stock" menuKey="stock" />
          <SidebarItem icon={<FaChartLine />} label="Sales & Delivery" menuKey="sales" />
          <SidebarItem icon={<FaChartLine />} label="Sales Reports" menuKey="sales-list-and-report" />

          {role !== "Sales" && (
            <>
              {!collapsed && <div style={{ fontSize: "11px", color: "#64748b", margin: "18px 10px 8px" }}>ORDERS & REQUESTS</div>}
              <SidebarItem icon={<FaClipboardList />} label="Purchase Requests" menuKey="requests" />
              <SidebarItem icon={<FaReceipt />} label="Paid Receipts" menuKey="view-receipt" />
            </>
          )}
        </Nav>
      </div>

      {/* Main Content */}
      <div style={{
        marginLeft: collapsed ? "80px" : "260px",
        padding: "20px",
        width: "100%",
        transition: "0.3s"
      }}>

        <Navbar className="shadow-sm mb-4 py-3 px-3" style={{ background: "linear-gradient(90deg,#0ea5e9,#2563eb)", borderRadius: "10px" }}>
          <Navbar.Brand className="fw-bold text-white">{role} Dashboard</Navbar.Brand>

          <Nav className="ms-auto align-items-center">
            <button onClick={() => setDarkMode(!darkMode)} style={{ background: darkMode ? "#1e293b" : "#e2e8f0", border: "none", borderRadius: "20px", padding: "6px 12px", marginRight: "15px" }}>
              {darkMode ? "🌙" : "☀"}
            </button>

            <Badge count={notifications} offset={[0, 5]}>
              <span style={{ fontSize: "20px", marginRight: "20px", cursor: "pointer" }}>🔔</span>
            </Badge>

            <Navbar.Text className="text-white me-3"><strong>{adminData?.name}</strong></Navbar.Text>

            <NavDropdown title={<span className="text-white">Account</span>} align="end">
              <NavDropdown.Item onClick={() => setActiveMenu("edit-profile")}>Edit Profile</NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout}>Logout</NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar>

        <div className="bg-white shadow-sm rounded-3 p-4">{renderContent()}</div>
      </div>
    </div>
  );
}

export default ProductionDashboardLayout;
